/** @module @liquidefy/code-editor/plugins/plugins @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  PluginId,
  PluginManifest,
  PluginState,
  PluginStatus,
  PluginContributions,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asPluginId,
  now,
  emptyPluginState,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL STATE TYPES — PluginRegistryState (local to this module)
// ═══════════════════════════════════════════════════════════════════════════════

/** Registry holding all known plugins keyed by PluginId. */
export interface PluginRegistryState {
  readonly plugins: Map<PluginId, PluginState>;
  readonly loadOrder: PluginId[];
  readonly createdAt: number;
}

/** Summary counts by status. */
export interface PluginStats {
  readonly total: number;
  readonly active: number;
  readonly disabled: number;
  readonly error: number;
  readonly installing: number;
  readonly updating: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 REGISTRY FACTORY — createEmptyRegistry
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Creates a fresh, empty plugin registry.
 * @returns PluginRegistryState with no plugins registered.
 */
export function createEmptyRegistry(): PluginRegistryState {
  return {
    plugins: new Map(),
    loadOrder: [],
    createdAt: now(),
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 PLUGIN CREATION — createPluginState, validateManifest
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Creates a new PluginState from a manifest. Validates the manifest first.
 * @param manifest - The plugin manifest to validate and wrap.
 * @returns EditorResult wrapping the new PluginState, or failure if manifest is invalid.
 */
export function createPluginState(manifest: PluginManifest): EditorResult<PluginState> {
  const validation = validateManifest(manifest);
  if (!validation.success) return validation as unknown as EditorResult<PluginState>;
  const id = asPluginId(generateId('plugin-'));
  const state = emptyPluginState(id, manifest);
  return ok(state, 'PLUGIN_CREATED', `Plugin "${manifest.name}" state created`);
}

/**
 * Validates a plugin manifest for required fields and correct shape.
 * @param manifest - Manifest to validate.
 * @returns EditorResult<PluginManifest> or failure with validation error.
 */
export function validateManifest(manifest: PluginManifest): EditorResult<PluginManifest> {
  if (!manifest.name || typeof manifest.name !== 'string') {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "name" string');
  }
  if (!manifest.version || typeof manifest.version !== 'string') {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "version" string');
  }
  if (!/^\d+\.\d+\.\d+/.test(manifest.version)) {
    return err('INVALID_MANIFEST', `Version "${manifest.version}" is not valid semver`);
  }
  if (!manifest.displayName) {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "displayName"');
  }
  if (!manifest.description) {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "description"');
  }
  if (!manifest.author) {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "author"');
  }
  if (!manifest.main) {
    return err('INVALID_MANIFEST', 'Manifest must have a non-empty "main" entry point');
  }
  if (!Array.isArray(manifest.activationEvents)) {
    return err('INVALID_MANIFEST', 'Manifest "activationEvents" must be an array');
  }
  if (!manifest.contributes || typeof manifest.contributes !== 'object') {
    return err('INVALID_MANIFEST', 'Manifest must have a "contributes" object');
  }
  return ok(manifest, 'MANIFEST_VALID', `Manifest "${manifest.name}" is valid`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 REGISTRY QUERIES — getPluginById, listPlugins
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Retrieves a plugin from the registry by its ID.
 * @param registry - Registry to search.
 * @param id - Target plugin ID.
 * @returns PluginState or null if not found.
 */
export function getPluginById(registry: PluginRegistryState, id: PluginId): PluginState | null {
  return registry.plugins.get(id) ?? null;
}

/**
 * Lists plugins from the registry, optionally filtered by status or capability.
 * @param registry - Registry to list from.
 * @param filter - Optional filter with `status` or `capability` constraint.
 * @returns Array of PluginState matching the filter.
 */
export function listPlugins(
  registry: PluginRegistryState,
  filter?: { status?: PluginStatus; capability?: string }
): PluginState[] {
  const all = Array.from(registry.plugins.values());
  if (!filter) return all;
  return all.filter((p) => {
    if (filter.status && p.status !== filter.status) return false;
    if (filter.capability) {
      const caps = p.manifest.permissions as string[];
      if (!caps.includes(filter.capability)) return false;
    }
    return true;
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 REGISTRY MUTATIONS — updatePluginStatus
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Updates the status of a plugin in the registry.
 * @param state - Current registry state.
 * @param id - Plugin ID to update.
 * @param status - New status to apply.
 * @returns EditorResult wrapping updated PluginState, or failure if plugin not found.
 */
export function updatePluginStatus(
  state: PluginRegistryState,
  id: PluginId,
  status: PluginStatus
): EditorResult<PluginState> {
  const plugin = state.plugins.get(id);
  if (!plugin) {
    return err('PLUGIN_NOT_FOUND', `Plugin "${id}" not found in registry`);
  }
  const updated: PluginState = { ...plugin, status };
  return ok(updated, 'PLUGIN_STATUS_UPDATED', `Plugin "${plugin.manifest.name}" status updated to "${status}"`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 CONTRIBUTIONS — getPluginContributions
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Returns the contribution points declared by a plugin.
 * @param state - Registry to search.
 * @param id - Plugin ID.
 * @returns PluginContributions, or empty contributions if plugin not found.
 */
export function getPluginContributions(
  state: PluginRegistryState,
  id: PluginId
): PluginContributions {
  const plugin = state.plugins.get(id);
  if (!plugin) return {};
  return plugin.contributions;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 DEPENDENCY RESOLUTION — resolvePluginDependencies, detectConflicts
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Resolves the load order for a plugin and all its transitive dependencies.
 * Returns plugins sorted in dependency-first topological order.
 * @param registry - Registry containing all available plugins.
 * @param id - Root plugin ID to resolve.
 * @returns EditorResult wrapping sorted PluginState[], or failure if deps are missing/cyclic.
 */
export function resolvePluginDependencies(
  registry: PluginRegistryState,
  id: PluginId
): EditorResult<PluginState[]> {
  const root = registry.plugins.get(id);
  if (!root) {
    return err('PLUGIN_NOT_FOUND', `Plugin "${id}" not found in registry`);
  }

  const visited = new Set<PluginId>();
  const stack = new Set<PluginId>();
  const order: PluginState[] = [];

  function visit(pluginId: PluginId): string | null {
    if (stack.has(pluginId)) return `Cyclic dependency detected at "${pluginId}"`;
    if (visited.has(pluginId)) return null;
    visited.add(pluginId);
    stack.add(pluginId);

    const p = registry.plugins.get(pluginId);
    if (!p) {
      stack.delete(pluginId);
      return `Missing dependency: plugin "${pluginId}" not found in registry`;
    }

    for (const depName of Object.keys(p.manifest.dependencies)) {
      // Find by name
      const dep = Array.from(registry.plugins.values()).find(
        (x) => x.manifest.name === depName
      );
      if (!dep) {
        stack.delete(pluginId);
        return `Missing dependency "${depName}" required by "${p.manifest.name}"`;
      }
      const cycleErr = visit(dep.id);
      if (cycleErr) {
        stack.delete(pluginId);
        return cycleErr;
      }
    }

    stack.delete(pluginId);
    order.push(p);
    return null;
  }

  const cycleErr = visit(id);
  if (cycleErr) return err('DEPENDENCY_ERROR', cycleErr);

  return ok(order, 'DEPS_RESOLVED', `Resolved ${order.length} plugins in load order for "${root.manifest.name}"`);
}

/**
 * Detects plugins in the registry that would conflict with a new plugin.
 * Conflicts are determined by duplicate command IDs or duplicate language grammar scope names.
 * @param registry - Existing registry.
 * @param newPlugin - The plugin candidate to check.
 * @returns Array of conflicting PluginState entries.
 */
export function detectConflicts(
  registry: PluginRegistryState,
  newPlugin: PluginState
): PluginState[] {
  const conflicts: PluginState[] = [];
  const newCommands = new Set(
    (newPlugin.contributions.commands ?? []).map((c) => c.id)
  );
  const newGrammars = new Set(
    (newPlugin.contributions.grammars ?? []).map((g) => g.scopeName)
  );

  for (const existing of registry.plugins.values()) {
    if (existing.id === newPlugin.id) continue;
    const existingCommands = (existing.contributions.commands ?? []).map((c) => c.id);
    const existingGrammars = (existing.contributions.grammars ?? []).map((g) => g.scopeName);

    const commandConflict = existingCommands.some((cid) => newCommands.has(cid));
    const grammarConflict = existingGrammars.some((s) => newGrammars.has(s));

    if (commandConflict || grammarConflict) {
      conflicts.push(existing);
    }
  }
  return conflicts;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 STATS — getPluginStats
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Returns a count summary of plugins grouped by status.
 * @param registry - Registry to summarize.
 * @returns PluginStats with counts for each status bucket.
 */
export function getPluginStats(registry: PluginRegistryState): PluginStats {
  let active = 0, disabled = 0, error = 0, installing = 0, updating = 0;

  for (const p of registry.plugins.values()) {
    switch (p.status) {
      case 'active': active++; break;
      case 'disabled': disabled++; break;
      case 'error': error++; break;
      case 'activating':
      case 'installed':
      case 'discovered': installing++; break;
      case 'updating': updating++; break;
    }
  }

  return {
    total: registry.plugins.size,
    active,
    disabled,
    error,
    installing,
    updating,
  };
}
